import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Settings, ShieldCheck, Check } from 'lucide-react';
interface ConsentScreenProps {
  onAgree: () => void;
  onBack: () => void;
}
export function ConsentScreen({
  onAgree,
  onBack
}: ConsentScreenProps) {
  const [agreed, setAgreed] = useState(false);
  return <div className="relative w-full h-screen overflow-hidden bg-slate-900" data-id="element-1">
      {/* Background */}
      <div className="absolute inset-0 z-0" data-id="element-2">
        <img src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1920&q=80" alt="Interior background" className="w-full h-full object-cover opacity-20 blur-sm" data-id="element-3" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 to-slate-900/70" data-id="element-4" />
      </div>

      {/* Header */}
      <div className="absolute top-6 left-6 right-6 z-50 flex items-center justify-between" data-id="element-5">
        <button onClick={onBack} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors" data-id="element-6">
          <ArrowLeft className="w-5 h-5" data-id="element-7" />
          <span className="text-sm" data-id="element-8">Back</span>
        </button>
        <button className="p-3 rounded-full bg-slate-800/50 backdrop-blur-sm border border-white/10 hover:bg-slate-700/50 transition-colors" data-id="element-9">
          <Settings className="w-6 h-6 text-white" data-id="element-10" />
        </button>
      </div>

      {/* Content */}
      <div className="relative z-10 flex items-center justify-center h-full px-6" data-id="element-11">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5
      }} className="bg-slate-800/80 backdrop-blur-xl rounded-2xl p-8 md:p-10 border border-white/10 shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col" data-id="element-12">
          {/* Icon + Heading */}
          <div className="flex flex-col items-center mb-6 flex-shrink-0" data-id="element-13">
            <div className="w-14 h-14 bg-cyan-500/20 rounded-full flex items-center justify-center mb-4" data-id="element-14">
              <ShieldCheck className="w-7 h-7 text-cyan-400" data-id="element-15" />
            </div>
            <h2 className="text-2xl font-bold text-white text-center" data-id="element-16">
              Artistic Representation Disclaimer
            </h2>
          </div>

          {/* Scrollable disclaimer body */}
          <div className="overflow-y-auto custom-scrollbar pr-2 space-y-4 text-white/80 text-sm leading-relaxed flex-1 min-h-0" data-id="element-17">
            <p data-id="element-18">
              Please be advised that all renderings, floor plans, virtual tours,
              and other visual representations displayed within this software
              are artist's conceptions and are for illustrative purposes only.
              They are not intended to be an exact depiction of the buildings,
              landscaping, or final product.
            </p>
            <p data-id="element-19">
              Features, specifications, materials, finishes, colors, and
              dimensions are approximate and are subject to change without
              notice. The actual view from any unit may vary. Furnishings,
              décor, and appliances are shown for demonstration purposes only
              and are not included unless specified in the purchase agreement.
            </p>
            <p data-id="element-20">
              Users should rely on the information contained in the official
              sales and legal documents for accurate details.
            </p>
          </div>

          {/* Consent checkbox */}
          <div className="mt-6 flex-shrink-0" data-id="element-21">
            <label className="flex items-start gap-3 cursor-pointer group mb-6" data-id="element-22">
              <button type="button" onClick={() => setAgreed(!agreed)} aria-pressed={agreed} aria-label="I understand and agree to the disclaimer" className={`mt-0.5 w-6 h-6 flex-shrink-0 rounded-md border flex items-center justify-center transition-all ${agreed ? 'bg-cyan-500 border-cyan-500' : 'bg-slate-700/50 border-white/20 group-hover:border-white/40'}`} data-id="element-23">
                {agreed && <Check className="w-4 h-4 text-white" data-id="element-24" />}
              </button>
              <span className="text-sm text-white/70 group-hover:text-white/90 transition-colors select-none" onClick={() => setAgreed(!agreed)} data-id="element-25">
                I understand that all visuals are artistic representations and I
                agree to the terms outlined above.
              </span>
            </label>

            <button onClick={onAgree} disabled={!agreed} className="w-full px-8 py-3 bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 disabled:from-slate-700 disabled:to-slate-600 text-white font-medium rounded-lg transition-all disabled:cursor-not-allowed disabled:opacity-50 uppercase tracking-wider" data-id="element-26">
              Agree &amp; Continue
            </button>

            <p className="text-center text-white/30 text-xs mt-4" data-id="element-27">
              2025 ResVR. All Rights Reserved.
            </p>
          </div>
        </motion.div>
      </div>
    </div>;
}